'use client';

import React, { useMemo, useState } from 'react';

import { Avatar as UIAvatar } from '@nextui-org/avatar';
import {
  Navbar as NextNavbar,
  NavbarBrand,
  NavbarContent,
  NavbarMenu,
  NavbarMenuItem,
  NavbarMenuToggle,
} from '@nextui-org/react';
import {
  Heart,
  Home,
  Images,
  PhoneCallIcon,
  Settings,
  ShoppingCartIcon,
  User,
  UserPlus,
  Info,
} from 'lucide-react';
import { useIsAdmin } from '@/shared/hooks/is-admin';
import { useIsAuthorizated } from '@/shared/hooks/is-authorizated';
import { useDisplayProfile } from '@/shared/hooks/use-display-profile';
import { Link } from '@/shared/navigation';
import { usePathname } from '@/shared/navigation';
import { SITE_BRAND } from '@/shared/constants/site-brand';
import { ROUTES } from '@/shared/routes/routes';
import { externalAvatarImgProps } from '@/shared/const/telegram-account';
import { cn } from '@/shared/utils/utils';
import { Spinner } from '@nextui-org/spinner';
import Image from 'next/image';

export const MobileHeader = ({
  title,
  right: _right,
  back: _back,
  isLogo: _isLogo = false,
  withShadow: _withShadow = true,
  rightExtra: _rightExtra,
}: {
  title: string | React.ReactNode;
  back?: React.ReactNode;
  right?: React.ReactNode;
  isLogo?: boolean;
  withShadow?: boolean;
  rightExtra?: React.ReactNode;
}) => {
  const pathname = usePathname();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isAuthorizated } = useIsAuthorizated();

  // const [position, setPosition] = useState(document.body?.scrollTop ?? 0);
  // const [visible, setVisible] = useState(true);
  // useEffect(() => {
  //   // TODO: fix неработающего throttle
  //   const handleScroll = throttle(() => {
  //     let moving = window?.pageYOffset ?? 0;
  //     if (position > moving) {
  //       setVisible(true);
  //     } else if (position < moving && moving - position > 10) {
  //       setVisible(false);
  //     }
  //     setPosition(moving);
  //   }, 1000);
  //   window.addEventListener('scroll', handleScroll);
  //   return () => {
  //     window?.removeEventListener('scroll', handleScroll);
  //   };
  // }, [position]);
  //
  // const cls = visible ? '' : 'top-[-600px]';

  const { isAdmin } = useIsAdmin();
  const {
    image: avatarSrc,
    displayName: profileDisplayName,
    isPending: profileIsPending,
  } = useDisplayProfile();

  const Avatar = useMemo(() => {
    return (
      <>
        {profileIsPending ? (
          <Spinner>
            <User size={32} strokeWidth={1.25} />
          </Spinner>
        ) : (
          <>
            {avatarSrc ? (
              <UIAvatar
                fallback={<UserPlus size={32} strokeWidth={1.25} />}
                size={'lg'}
                isBordered
                as="button"
                className="transition-transform"
                color="secondary"
                name={profileDisplayName}
                src={avatarSrc}
                imgProps={externalAvatarImgProps}
              />
            ) : (
              <User size={32} strokeWidth={1.25} />
            )}
          </>
        )}
      </>
    );
  }, [avatarSrc, profileDisplayName, profileIsPending]);

  const mobileMenuItems = useMemo(() => {
    const items: {
      id: string;
      href: string;
      title: string;
      icon: React.FC;
    }[] = [
      {
        id: ROUTES.home,
        href: ROUTES.home,
        title: 'Главная',
        icon: Home,
      },
      {
        id: ROUTES.masterclasses,
        href: ROUTES.masterclasses,
        title: 'Мастер-классы',
        icon: Images,
      },
      {
        id: ROUTES.about,
        href: ROUTES.about,
        title: 'Обо мне',
        icon: Info,
      },
      {
        id: ROUTES.favorites,
        href: ROUTES.favorites,
        title: 'Избранное',
        icon: Heart,
      },
      {
        id: ROUTES.contacts,
        href: ROUTES.contacts,
        title: 'Контакты',
        icon: PhoneCallIcon,
      },
      {
        id: ROUTES.cart,
        href: ROUTES.cart,
        title: 'Корзина',
        icon: ShoppingCartIcon,
      },
    ];

    if (isAuthorizated) {
      items.push({
        id: ROUTES.meMasterclasses,
        href: ROUTES.meMasterclasses,
        title: 'Мои покупки',
        icon: Images,
      });

      items.push({
        id: ROUTES.me,
        href: ROUTES.me,
        title: 'Профиль',
        icon: () => Avatar,
      });
    }

    if (isAdmin) {
      items.push({
        id: ROUTES['admin-front'],
        href: ROUTES['admin-front'],
        title: 'Администрирование',
        icon: Settings,
      });
    }
    return items;
  }, [Avatar, isAdmin, isAuthorizated]);

  return (
    <>
      <NextNavbar
        onMenuOpenChange={setIsMenuOpen}
        isMenuOpen={isMenuOpen}
        className={cn(
          'z-50 border-b fixed  top-0 left-0 right-0 transition-all duration-300',
        )}
        classNames={{
          wrapper: 'w-full p-0 bg-background/90',
        }}
      >
        <NavbarContent>
          <>
            <NavbarMenuToggle
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              className="sm:hidden"
            />
            <NavbarBrand>
              <Link
                prefetch
                className="flex items-center gap-2"
                href={ROUTES.home}
              >
                <Image width={50} height={50} src={'/logo.svg'} alt={'logo'} />
                <span className="text-lg text-gray-900 font-semibold">
                  {SITE_BRAND}
                </span>
              </Link>
            </NavbarBrand>
          </>
        </NavbarContent>
        <NavbarContent justify="center">{title}</NavbarContent>
        <NavbarMenu className="flex justify-center flex-col gap-4 items-center z-50">
          {mobileMenuItems.map((item, index) => {
            const isActive = pathname === item.href;
            return (
              <NavbarMenuItem isActive={isActive} key={`${item}-${index}`}>
                <Link prefetch key={item.title} href={item.href}>
                  <div className="flex gap-2">
                    <item.icon
                      // @ts-ignore
                      size={32}
                      strokeWidth={1.25}
                      className={`${isActive ? 'text-primary' : 'text-gray-900'}`}
                    />
                    <div
                      className={
                        !isActive
                          ? 'text-gray-900 text-xl'
                          : 'border-primary text-xl'
                      }
                    >
                      {item.title}
                    </div>
                  </div>
                </Link>
              </NavbarMenuItem>
            );
          })}
        </NavbarMenu>
      </NextNavbar>
    </>
  );
};
